<template>
  <div id="app all-requests">
    <div v-if="!loaded" class="loader">
      <div class="loading loading-lg"></div>
    </div>

    <template v-else>
      <div class="scroll-top" @click="scrollToTop">(^)</div>

      <div class="toast toast-warning" v-if="!updating && !processing && (needsRescrape || isExchangeOpen)">
        <span v-if="needsRescrape">
          Please re-scrape to populate this page!
        </span>

        <span v-if="isExchangeOpen">
          Prompt bookmarking is disabled until after signups are closed. Someone
          must run the scraper again at that time.
        </span>
      </div>

      <!-- FILTERS -->

      <div role="search" class="filters mt-2">
        <div class="text-assistive">Search filters</div>

        <div class="form-horizontal columns">
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="fandom-filter">Fandom:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="fandom-filter" placeholder="Search fandoms..."
                v-model="fandomFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="freeform-filter">Freeforms:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="freeform-filter" placeholder="Search freeforms..."
                v-model="freeformFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="optional-tags-filter">Optional Tags:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="optional-tags-filter"
                placeholder="Search optional tags..." v-model="optionalTagFilter" />
            </div>
          </div>
        </div>

        <div class="form-horizontal columns">
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="character-filter">Characters:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="character-filter" placeholder="Search characters..."
                v-model="characterFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="relationship-filter">Relationships:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="relationship-filter"
                placeholder="Search relationships..." v-model="relationshipFilter" />
            </div>
          </div>

          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="prompt-filter">Prompt:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="prompt-filter" placeholder="Search prompt text..."
                v-model="promptFilter" />
            </div>
          </div>
        </div>

        <div class="form-horizontal columns">
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="character-filter">Archive Warnings:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="warning-filter" placeholder="Search warnings..."
                v-model="warningFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="ratings-filter">Ratings:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="ratings-filter" placeholder="Search ratings..."
                v-model="ratingFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="user-filter">Username:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="user-filter" placeholder="Search username..."
                v-model="usernameFilter" />
            </div>
          </div>
        </div>

        <span>
          <button ref="permalink" class=" mr-2 btn btn-sm permalink" @click="copyShareableLink"><span
              class="fas fa-link"></span>
            <span class="fas fa-check"></span>
            Permalink</button>
        </span>

        <label class="form-switch form-inline">
          <input type="checkbox" v-model="options.useToggles" />
          <i class="form-icon"></i> Use toggles for optional details
        </label>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="options.truncateTags" />
          <i class="form-icon"></i> Use toggles for optional/freeform tags
        </label>
        <label v-if="options.truncateTags" class="max-tags-label form-inline form-label mr-2" for="fandom-filter">after
          <input class="form-input form-inline max-tags" id="max-tags" type="number" placeholder="10" min="0"
            v-model="options.tagLimit">
          tags
        </label>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="options.openDetails" />
          <i class="form-icon"></i> Auto-expand details
          <span class="tooltip c-hand" data-tooltip="Opens HTML details elements">
            <sup>
              <i class="fa fa-question-circle" aria-hidden="true"></i>
            </sup>
          </span>
        </label>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="sortByFandom" />
          <i class="form-icon"></i> Sort by fandom
        </label>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="addOptionals" />
          <i class="form-icon"></i> Search for Optionals in Freeforms as well
        </label>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="dedupeByFandom" />
          <i class="form-icon"></i> De-dupe requests with multiple fandoms
        </label>



        <p class="text-small">
          This view loads <em>all</em> prompts into memory and can slow down your device for large challenges. Search
          terms are
          AND by default. You can use syntax like | for OR, ! for NOT, and
          ="exact match". Prompt meme requests will have a best-guess link to the AO3 page the prompt might be on - the
          more recent the scrape, the better the guess.
        </p>
      </div>

      <div class="divider"></div>

      <!-- END FILTERS -->

      <div v-if="updating" class="loader">
        <div class="loading loading-lg"></div>
      </div>

      <div class="toast toast-warning" v-if="processing && exchangeId">
        The <strong>last successful scrape is being shown while a new
          scrape
          is in-progress</strong>. Caveat lector. Prompt bookmarking is disabled until after signups are closed. Someone
        must re-scrape
        again
        at that time.
      </div>

      <pagination :pagination="pagination" @page="page"></pagination>

      <table class="table table-striped" v-if="!updating" aria-label="Requests table">
        <thead>
          <tr>
            <th class="prompts">Requests</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="prompt in filtered" :key="prompt['sha']">
            <td class="prompts">
              <div class="prompt">
                <div class="flex user-data">
                  <div class="flex-25">
                    <button aria-label="Toggle prompt bookmark" v-if="!isExchangeOpen" class="bookmark btn btn-link"
                      @click="togglePromptmark(prompt)">
                      <span v-if="hasPromptmark(prompt)" aria-label="Remove bookmark" class="fas fa-heart"></span>
                      <span v-else class="far fa-heart" aria-label="Bookmark"></span>
                    </button>

                    <strong>
                      <router-link :to="`/${exchangeId}/user/${prompt.username}`">{{ prompt.username }}</router-link>
                    </strong>
                  </div>

                  <div class="text-bold">{{ prompt.fandom }} <router-link v-if="isAggregable(prompt.fandom)" class=""
                      :to="`/everything/username/${prompt.username}?groupby=fandom`"
                      title="See all previously requested prompts for this users"><i
                        class="fas fa-external-link-alt"></i></router-link></div>
                </div>


                <div class="flex">
                  <character-list class="pr-2" :tags="false" :characters="prompt.characters"
                    :relationships="prompt.relationships"></character-list>

                  <div>
                    <div class="mb-2">
                      <div class="mb-2" v-if="prompt.title">
                        <strong>Title:</strong>&nbsp;
                        <span>{{ prompt.title }}</span>
                      </div>
                      <chips v-if="prompt.fandoms && prompt.fandoms.length > 1"
                        :tags="prompt.fandoms.filter(f => f != prompt.fandom)" type="fandom" :exchangeId="exchangeId"
                        title="Other Fandoms"></chips>

                      <chips :tags="prompt.warnings" title="Warnings"></chips>

                      <chips :tags="prompt.ratings" title="Ratings"></chips>

                      <chips :tags="prompt.freeforms" title="Freeforms" :truncate="options.truncateTags"
                        :limit="options.tagLimit"></chips>

                      <chips :tags="prompt.optional_tags" title="Optional Tags" :truncate="options.truncateTags"
                        :limit="options.tagLimit">
                      </chips>
                    </div>
                    <p v-if="prompt.letter">
                      <a :href="prompt.letter" target="_blank">Letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                        <sup>
                          <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>
                    </p>


                    <template v-if="(!options.useToggles || shouldShowPrompt(prompt.sha)) &&
                      prompt.prompt
                    ">
                      <div v-html="prompt.prompt" class="prompt-text"></div>
                    </template>

                    <button class="btn btn-sm" v-if="options.useToggles && prompt.prompt"
                      @click="togglePrompt(prompt.sha)">
                      <span v-if="shouldShowPrompt(prompt.sha)">Show less
                      </span>
                      <span v-else>Read optional details
                      </span>
                    </button>

                    <p v-else-if="!prompt.prompt">
                      No optional details given.
                      <span v-if="prompt.letter">
                        However, they do have a
                        <a :href="prompt.letter" target="blank">letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                          <sup>
                            <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>.
                      </span>
                    </p>

                    <div>
                      <a class="btn btn-sm mt-2" v-if="prompt.page && Number(prompt.page) > 0"
                        :href="`https://archiveofourown.org/collections/${exchangeId}/requests?page=${prompt.page}&sort_column=created_at&sort_direction=ASC`"
                        target="_blank">
                        Approximate AO3 prompt page
                      </a>
                    </div>
                    <!-- <template v-if="prompt.prompt">
                      <div v-html="prompt.prompt" class="prompt-text"></div>
                    </template>

                    <p v-else-if="!prompt.prompt">
                      No optional details given.
                      <span v-if="prompt.letter">
                        However, they do have a
                        <a :href="formatUrl(prompt.letter)" target="blank"
                          >letter
                        </a>
                        <span
                          class="tooltip c-hand"
                          data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page."
                        >
                          <sup>
                            <i
                              class="fa fa-question-circle"
                              aria-hidden="true"
                            ></i> </sup></span
                        >.
                      </span>
                    </p> -->
                  </div>
                </div>

                <hr class="break" />
              </div>
              <!-- <span v-else>No prompts ):</span> -->
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <pagination :pagination="pagination" @page="page"></pagination>

    <textarea id="copyInput" ref="copyInput"></textarea>
  </div>
</template>

<script>
import utils from "../components/utils.js";
import chips from "../components/chips.vue";
import characterList from "../components/character-list.vue";
import Pagination from "../components/pagination.vue";

import {
  uniqWith,
  debounce,
  intersection,
  sortBy,
  each,
  includes,
  remove
} from "lodash-es";
import { mapGetters } from "vuex";
import axios from "axios";
import Fuse from "fuse.js";
const delay = ms => new Promise(res => setTimeout(res, ms));
let fandomSearch;
let freeformSearch;
let relationshipSearch;
let optionalTagsSearch;
let charactersSearch;
let promptSearch;
let warningSearch;
let ratingSearch;
let userSearch;
function getFn(obj, path) {
  //   // Use the default `get` function
  const value = Fuse.config.getFn(obj, path);
  //   // ... do something with `value`

  if (Array.isArray(value)) {
    return value.map(el => normalizeStr(el));
  }
  return normalizeStr(value);
}
const fuseOptions = {
  shouldSort: true,
  threshold: 0,
  ignoreLocation: true,
  useExtendedSearch: true,
  getFn
};

// Remove english articles from fandom names
function removeArticlesCompare(o) {
  if (!o) {
    return;
  }
  const regex = /^(the\s|a\s|an\s)/i;
  if (!o.name) {
    return o;
  }
  return o.name.toLowerCase().replace(regex, "");
}

function normalizeStr(str) {
  if (!str || typeof str !== "string") {
    return;
  }
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export default {
  name: "all",
  components: { chips, characterList, Pagination },
  beforeMount() {
    this.$store.commit("setAllRequests", []);
    this.load();
  },
  created() {
    this.loadFandomBookmarksAndPromptmarks();
    this.updateFilter();
    // window.addEventListener("scroll", this.handleScroll);
  },
  updated() {
    if (this.options.openDetails) {
      utils.openDetails();
    }
  },
  // destroyed() {
  //   window.removeEventListener("scroll", this.handleScroll);
  // },
  data() {
    return {
      scraperRes: undefined,
      scraperLoading: false,
      processing: false,
      showPrompt: [],
      status: {},
      open: true,
      filtered: [],
      updating: true,
      sticky: false,
      loaded: false,
      fandomFilter: undefined,
      freeformFilter: undefined,
      relationshipFilter: undefined,
      optionalTagFilter: undefined,
      characterFilter: undefined,
      promptFilter: undefined,
      warningFilter: undefined,
      ratingFilter: undefined,
      usernameFilter: undefined,
      scrollIndex: 50,
      totalFound: 0,
      addOptionals: true,
      sortByFandom: false,
      dedupeByFandom: false,
      pagination: {
        first: 0,
        last: 100,
        size: 100,
        total: 0
      },
    };
  },
  computed: {
    ...mapGetters([
      "exchangeId",
      "isExchangeOpen",
      "allRequests",
      "bookmarks",
      "promptmarks",
      "timestamp",
      "options",
    ]),
    needsRescrape() {
      return new Date(this.timestamp) < new Date("2021-07-08T00:00:00.000Z");
    }
  },
  watch: {
    async $route(to) {
      if (to.name === "allrequests") {
        this.scrollIndex = 50;
        this.totalFound = 0;

        this.load();
      }
    },
    fandomFilter() {
      this.updateFilter();
    },
    characterFilter() {
      this.updateFilter();
    },
    freeformFilter() {
      this.updateFilter();
    },
    optionalTagFilter() {
      this.updateFilter();
    },
    relationshipFilter() {
      this.updateFilter();
    },
    promptFilter() {
      this.updateFilter();
    },
    warningFilter() {
      this.updateFilter();
    },
    ratingFilter() {
      this.updateFilter();
    },
    usernameFilter() {
      this.updateFilter();
    },
    dedupeByFandom() {
      this.updateFilter();
    },
    addOptionals(val) {
      if (val) {
        optionalTagsSearch = freeformSearch = new Fuse(this.allRequests, {
          keys: ["optional_tags", "freeforms"],
          ...fuseOptions
        });
      } else {
        optionalTagsSearch = new Fuse(this.allRequests, {
          keys: ["optional_tags"],
          ...fuseOptions
        });
        freeformSearch = new Fuse(this.allRequests, {
          keys: ["freeforms"],
          ...fuseOptions
        });
      }

      this.updateFilter();
    },
    sortByFandom() {
      this.updateFilter();
    },
    options: {
      handler(val) {
        if (val.openDetails) {
          utils.openDetails();
        }
        this.$store.commit("setOptions", val);
        this.$localStorage.set("appOptions", JSON.stringify(val));
      },
      deep: true
    }
  },
  methods: {
    ...utils,
    sortBy,
    page(start) {
      this.pagination.last = start + this.pagination.size;
      this.pagination.first = start;
      this.updateFilter(false);
    },
    handleScroll() {
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = document.documentElement;

      if (
        scrollTop + clientHeight >= scrollHeight - 5 &&
        this.allRequests.length > this.scrollIndex
      ) {
        const newIndex = this.scrollIndex + 50;
        if (newIndex < this.allRequests.length) {
          this.scrollIndex = newIndex;
        } else {
          this.scrollIndex = this.allRequests.length;
        }

        this.updateFilter(false);
      }
    },
    async load() {
      await this.redirectExchangeId(this.$route);

      this.status = {};
      this.scraperRes = undefined;
      this.scraperLoading = false;
      this.processing = false;
      this.updating = false;
      this.loaded = false;
      this.$store.commit("setAllRequests", []);

      this.updateFilterFromParams(this.$router.currentRoute);

      this.filtered = [];

      const exchangeId = this.$route.params.exchangeId;
      this.$store.commit("setExchangeId", this.$route.params.exchangeId);

      axios
        .get(
          `https://autoao3.herokuapp.com/db/exchanges/${exchangeId}/status`
        )
        .then(res => {
          if (
            res.data &&
            res.data.status != "open" &&
            res.data.status != "complete" &&
            res.data.status != "unparseable"
          ) {
            this.processing = true;
            this.status = res.data;
          }

          this.$store.commit("setTimestamp", res.data.scraped);

          this.$store.commit(
            "setIsExchangeOpen",
            res.data && (res.data.status == "open" || res.data.status == "processing")
          );
        });

      // Auctionbot
      if (exchangeId == 'AuctionsAsThreatened' || exchangeId == 'AAT_Request_Overflow') {

        const otherExchange = exchangeId == 'AuctionsAsThreatened' ? 'AAT_Request_Overflow' : 'AuctionsAsThreatened';
        axios
          .get(
            `https://autoao3.herokuapp.com/db/exchange/${exchangeId}`
          )
          .then(res => {
            let result = res.data


            axios.get(
              `https://autoao3.herokuapp.com/db/exchange/${otherExchange}`

            )
              .then(res2 => {
                result = result.concat(res2.data);
                this.$store.commit("setAllRequests", result);

                this.loaded = true;
                fandomSearch = new Fuse(result, {
                  keys: ["fandom", "fandoms"],
                  ...fuseOptions
                });
                relationshipSearch = new Fuse(result, {
                  keys: ["relationships"],
                  ...fuseOptions
                });
                freeformSearch = optionalTagsSearch = new Fuse(result, {
                  keys: ["optional_tags", "freeforms"],
                  ...fuseOptions
                });

                charactersSearch = new Fuse(result, {
                  keys: ["characters"],
                  ...fuseOptions
                });

                // optionalTagsSearch = new Fuse(result, {
                //   keys: ["optionalTags"],
                //   ...fuseOptions
                // });

                userSearch = new Fuse(result, {
                  keys: ["username"],
                  ...fuseOptions
                });

                promptSearch = new Fuse(result, {
                  keys: ["prompt"],
                  ...fuseOptions
                });

                warningSearch = new Fuse(result, {
                  keys: ["warnings"],
                  ...fuseOptions
                });

                ratingSearch = new Fuse(result, {
                  keys: ["ratings"],
                  ...fuseOptions
                });

                userSearch = new Fuse(result, {
                  keys: ["username"],
                  ...fuseOptions
                });

                this.updateFilter();

              })

          });

      } else {

        axios
          .get(
            `https://autoao3.herokuapp.com/db/exchange/${exchangeId}`
          )
          .then(res => {
            let result = res.data
            this.$store.commit("setAllRequests", result);

            this.loaded = true;
            fandomSearch = new Fuse(result, {
              keys: ["fandom"],
              ...fuseOptions
            });
            relationshipSearch = new Fuse(result, {
              keys: ["relationships"],
              ...fuseOptions
            });
            freeformSearch = optionalTagsSearch = new Fuse(result, {
              keys: ["optional_tags", "freeforms"],
              ...fuseOptions
            });

            charactersSearch = new Fuse(result, {
              keys: ["characters"],
              ...fuseOptions
            });

            // optionalTagsSearch = new Fuse(result, {
            //   keys: ["optionalTags"],
            //   ...fuseOptions
            // });

            userSearch = new Fuse(result, {
              keys: ["username"],
              ...fuseOptions
            });

            promptSearch = new Fuse(result, {
              keys: ["prompt"],
              ...fuseOptions
            });

            warningSearch = new Fuse(result, {
              keys: ["warnings"],
              ...fuseOptions
            });

            ratingSearch = new Fuse(result, {
              keys: ["ratings"],
              ...fuseOptions
            });

            userSearch = new Fuse(result, {
              keys: ["username"],
              ...fuseOptions
            });

            this.updateFilter();
          });

      }


      this.loadFandomBookmarksAndPromptmarks();
    },
    togglePrompt(sha) {
      if (this.shouldShowPrompt(sha)) {
        this.showPrompt = remove(this.showPrompt, sha);
      } else {
        this.showPrompt.push(sha);
      }
    },
    shouldShowPrompt(sha) {
      return includes(this.showPrompt, sha);
    },
    updateFilter: debounce(function (resetPagination = true) {
      this.updating = true;
      let arr = this.allRequests;

      if (resetPagination) {
        this.pagination.first = 0;
        this.pagination.last = this.pagination.size;
      }

      // Do not let pagination go below 0
      if (this.pagination.first < 0) {
        this.pagination.first = 0;
      }


      if (this.fandomFilter && this.fandomFilter.length) {
        const indices = fandomSearch.search(normalizeStr(this.fandomFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = res;
      }

      if (this.relationshipFilter && this.relationshipFilter.length) {
        const indices = relationshipSearch.search(
          normalizeStr(this.relationshipFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.characterFilter && this.characterFilter.length) {
        const indices = charactersSearch.search(
          normalizeStr(this.characterFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.optionalTagFilter && this.optionalTagFilter.length) {
        const indices = optionalTagsSearch.search(
          normalizeStr(this.optionalTagFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.freeformFilter && this.freeformFilter.length) {
        const indices = freeformSearch.search(
          normalizeStr(this.freeformFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.promptFilter && this.promptFilter.length) {
        const indices = promptSearch.search(normalizeStr(this.promptFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.warningFilter && this.warningFilter.length) {
        const indices = warningSearch.search(normalizeStr(this.warningFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.ratingFilter && this.ratingFilter.length) {
        const indices = ratingSearch.search(normalizeStr(this.ratingFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.usernameFilter && this.usernameFilter.length) {
        const indices = userSearch.search(normalizeStr(this.usernameFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.sortByFandom) {
        arr = sortBy(arr, p => p.fandom.toLowerCase(), [removeArticlesCompare]);

      } else {
        arr = sortBy(arr, p => p.username.toLowerCase(), [removeArticlesCompare]);
      }

      if (this.dedupeByFandom) {
        arr = uniqWith(arr, (a, b) => {
          return a.request_id == b.request_id
        })
      }

      this.totalFound = arr.length;

      arr = uniqWith(arr, (a, b) => {
        return a.sha == b.sha;
      })

      this.filtered = arr.slice(this.pagination.first, this.pagination.last);
      this.pagination.total = arr.length;

      this.updating = false;
    }, 300),
    scrollToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    process() {
      this.scraperLoading = true;
      axios
        .get(`https://autoao3.herokuapp.com/scrape/${this.exchangeId}`)
        .then(res => {
          this.scraperRes = res;
          this.scraperLoading = false;
        })
        .catch(e => {
          if (e.response) {
            this.scraperRes = e.response;
            this.scraperLoading = false;
            return;
          }
          this.scraperRes = {
            error: true,
            e: e,
            msg: "Something went wrong! Report a bug."
          };
          this.scraperLoading = false;
        });
    },
    async copyShareableLink() {

      let params = [];
      if (this.fandomFilter) {
        params.push(`fandom=${this.fandomFilter}`)
      }
      if (this.freeformFilter) {
        params.push(`freeforms=${this.freeformFilter}`)
      }
      if (this.characterFilter) {
        params.push(`characters=${this.characterFilter}`)
      }
      if (this.relationshipFilter) {
        params.push(`relationships=${this.relationshipFilter}`)
      }
      if (this.promptFilter) {
        params.push(`prompt=${this.promptFilter}`)
      }
      if (this.optionalTagFilter) {
        params.push(`optionaltags=${this.optionalTagFilter}`)
      }
      if (this.ratingFilter) {
        params.push(`rating=${this.ratingFilter}`)
      }
      if (this.warningFilter) {
        params.push(`warnings=${this.warningFilter}`)
      }
      if (this.usernameFilter) {
        params.push(`username=${this.usernameFilter}`)
      }

      const queryparams = params.length ?
        `?${params.join('&')}`
        : ''


      // this.$refs.copyInput.value = `${window.location.origin}/#/${this.exchangeId}/all?${queryparams}`
      this.$refs.copyInput.value = `https://autoao3app.fandom.tools/#/${this.exchangeId}/all${queryparams}`
      // Select the text field
      this.$refs.copyInput.select();
      this.$refs.copyInput.setSelectionRange(0, 99999); // For mobile devices

      // Copy the text inside the text field
      navigator.clipboard.writeText(this.$refs.copyInput.value);

      this.$refs.permalink.classList.add("active");
      await delay(400);

      this.$refs.permalink.classList.remove("active");


    }
  }
};
</script>

<style lang="scss">
@import "../styles/app.scss";
@import "../styles/mobile.scss";
</style>


<style lang="scss" scoped>
@import "../styles/variables.scss";

.prompt {
  margin: 0.8em 0;
  // border-bottom: 1px solid $outline-light;

  &:last-child {
    .break {
      display: none;
    }
  }
}

.bookmark,
.letter {
  height: 1.3em;
}

.user-data {
  font-size: larger;

  .letter {
    padding: 0;
    margin: 0;
    vertical-align: baseline;
  }

  .bookmark {
    position: relative;
    bottom: 4px;
    margin-right: 0.4em;
  }
}

.flex {
  margin-top: 0.4em;
  display: flex;
}

ul {
  li {
    margin: 0;
    padding: 4px 0;
    border-bottom: 1px solid $outline-light;

    &:last-child {
      border: 0;
    }

    &:first-child {
      padding-top: 0;
    }
  }
}

.flex-25,
.requested-characters {
  // margin-top: 1em;
  // margin-right: 1em;
  flex: 0 0 25%;
}

.toggle {
  width: 150px;
  height: 35px;
  display: block;
}

.break {
  width: 80%;
  margin: 0.8em auto;
}

.form-group.col-4 {
  &.form-group:not(:last-child) {
    margin-bottom: 0;
  }
}

@media screen and (max-width: 767px) {
  .flex {
    display: inherit;
  }

  .requested-characters {
    display: block;
    margin-bottom: 0.5em;
  }
}

.max-tags-label {
  margin-left: -0.2rem;
}

.max-tags {
  height: 1.2rem;
  line-height: 1.2rem;
  padding: 3px;
  width: 40px;
  margin-left: 0.25rem;
}

#copyInput {
  display: none;
}

.permalink {
  .fa-check {
    display: none;
  }

  &.active {

    .fa-link {
      display: none;
    }

    .fa-check {
      display: inline;
    }
  }
}
</style>